import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  ThemeProvider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { subdomain } from '../../auth/utilities';
import SnackbarAlert from '../../components/SnackbarAlert/SnackbarAlert';
import SuccessModal from '../../components/SuccessModal/SuccessModal';
import PolicyCard from './components/PolicyCard';
import PdfViewer from './components/PdfViewer';
import {
  AcknowledgementSuccess,
  TokenExpired,
  TokenInvalid
} from './components/AcknowledgementStatus';
import theme from '../../theme';
import OTPVerification from './components/OTPVerification';
import './PolicyAcknowledge.css';

const PolicyComponent = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") || "";
  const documentId = queryParams.get("ids") || "";

  const backend = (() => {
    const envBackend = process.env.REACT_APP_BACKEND;
    if (window.location.hostname === "localhost") {
      return (envBackend || "http://localhost:8000/api/").replace(/\/$/, '') + '/';
    }
    if (envBackend) return envBackend.replace(/\/$/, '') + '/';
    return "";
  })();

  const [policies, setPolicies] = useState([]);
  const [openedDocs, setOpenedDocs] = useState(() => {
    const saved = localStorage.getItem(`opened_docs_${token}_${documentId}`);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [error, setError] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [isVerified, setIsVerified] = useState(() => {
    return sessionStorage.getItem('policy_verified') === 'true';
  });

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [ackCompleted, setAckCompleted] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    autoDismiss: true
  });

  useEffect(() => {
    document.title = "WhizzC";
    setTokenExpired(false);
    setTokenInvalid(false);
    setIsAcknowledged(false);
    setError("");
    setOpenedDocs(() => {
      const saved = localStorage.getItem(`opened_docs_${token}_${documentId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    });
    fetchPolicies();
  }, [token, documentId]);

  useEffect(() => {
    localStorage.setItem(`opened_docs_${token}_${documentId}`, JSON.stringify(Array.from(openedDocs)));
  }, [openedDocs, token, documentId]);

  const fetchPolicies = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!backend) throw new Error("Backend URL is not configured.");
      if (!subdomain) throw new Error("Tenant subdomain is not configured.");
      if (!token || !documentId) throw new Error("Missing tracking token or policy IDs.");
      const response = await fetch(`${backend}${subdomain}/en/policy/ack-policy-list/?token=${token}&ids=${documentId}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const errorText = (data?.error || "").toString();
        const errorLower = errorText.toLowerCase();

        if (errorLower.includes("expired")) setTokenExpired(true);
        else if (errorLower.includes("invalid") || errorLower.includes("authentication token")) setTokenInvalid(true);
        else throw new Error(data?.error || `Server error: ${response.status}`);
        return;
      }
      const data = await response.json();
      setPolicies(data.documents || []);
      setIsAcknowledged((data.staff_info?.policy_status || "").trim().toLowerCase() === "completed");
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const viewPolicy = async (docId) => {
    setLoadingId(docId);
    try {
      const response = await fetch(`${backend}${subdomain}/en/policy/ack-view-policy/${docId}/?access=read&token=${token}&ids=${documentId}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error("Could not load policy.");
      const data = await response.json();
      setPdfUrl(data.pdf_url);
      setOpenedDocs(prev => new Set(prev).add(docId));
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to open document.", severity: "error" });
    } finally {
      setLoadingId(null);
    }
  };

  const handleAcknowledge = async () => {
    if (policies.length === 0 || openedDocs.size < policies.length) {
      setSnackbar({ open: true, message: "Please review all policies before acknowledging.", severity: "warning" });
      return;
    }
    setIsAcknowledging(true);
    try {
      const response = await fetch(`${backend}${subdomain}/en/policy/ack-policy-list/?token=${token}&ids=${documentId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ policy_status: "completed" }),
        credentials: 'include',
      });
      if (!response.ok) throw new Error("Submission failed.");
      setIsAcknowledged(true);
      setShowSuccessModal(true);

      // Auto-transition after 2 seconds (Legacy behavior)
      setTimeout(() => {
        handleOkay();
      }, 2000);
    } catch (err) {
      setSnackbar({ open: true, message: "Error submitting acknowledgement.", severity: "error" });
      setIsAcknowledging(false);
    }
  };

  const handleOkay = () => {
    setShowSuccessModal(false);
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setAckCompleted(true);
    }, 1500);
  };

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box className="loading-container">
          <CircularProgress size={40} />
        </Box>
      </ThemeProvider>
    );
  }

  if (ackCompleted) return <AcknowledgementSuccess />;
  if (tokenExpired) return <TokenExpired />;
  if (tokenInvalid) return <TokenInvalid />;

  if (!isVerified) {
    return (
      <ThemeProvider theme={theme}>
        <OTPVerification
          backend={backend}
          subdomain={subdomain}
          onVerified={() => {
            setIsVerified(true);
            fetchPolicies();
          }}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className="policy-page-root">
        {/* Header Container */}
        <Box className="policy-header-container">
          <Typography
            variant="h4"
            className="policy-header-title"
          >
            Organization Policy
          </Typography>

          {policies.length > 0 && (
            <Button
              onClick={handleAcknowledge}
              disabled={
                isAcknowledging ||
                (isAcknowledged || openedDocs.size !== policies.length)
              }
              startIcon={
                isAcknowledging ? (
                  <CircularProgress size={16} color="inherit" />
                ) : isAcknowledged ? (
                  <CheckCircleIcon />
                ) : (
                  <RadioButtonUncheckedIcon />
                )
              }
              className={`acknowledge-button ${isAcknowledged ? 'is-acknowledged' : ''}`}
            >
              {isAcknowledged ? "Acknowledged" : "Acknowledge"}
            </Button>
          )}
        </Box>

        {error && (
          <Box className="policy-error-box">
            <Typography color="error">
              {error}
            </Typography>
          </Box>
        )}

        {/* Policy Grid */}
        {policies.length > 0 ? (
          <Grid container spacing={2}>
            {policies.map((doc, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={doc.document_id}>
                <PolicyCard
                  doc={doc}
                  index={index}
                  isOpened={openedDocs.has(doc.document_id)}
                  isLoading={loadingId === doc.document_id}
                  onView={viewPolicy}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box className="no-policies-box">
            <Typography variant="h6" color="text.secondary">
              No policies available
            </Typography>
          </Box>
        )}

        {/* Shared UI Components */}
        <SnackbarAlert
          open={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          severity={snackbar.severity}
          autoDismiss={true}
        />

        <SuccessModal
          open={showSuccessModal}
          onOkay={handleOkay}
        />

        <PdfViewer
          url={pdfUrl}
          onClose={() => setPdfUrl("")}
        />

      </Box>
    </ThemeProvider >
  );
};

export default PolicyComponent;
